import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setActiveTownId, setToken, hasLoggedIn, markLoggedIn } from "../api";
import { useSession } from "../stores/session";

const GUEST_SAMPLE_NAMES = ["云游客", "过路人", "小旅人", "远来的星", "风尘旅人", "摘星客", "听风者", "星光旅人"];

type View = "auth" | "verify" | "forgot";
type FieldErrors = { email?: string; username?: string; password?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Auth() {
  // 曾经成功登录过的用户默认展示登录界面,新用户展示注册界面
  const [mode, setMode] = useState<"login" | "register">(hasLoggedIn() ? "login" : "register");
  const [view, setView] = useState<View>("auth");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [guestExpanded, setGuestExpanded] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestGender, setGuestGender] = useState<"male" | "female">("male");
  const [genderCounts, setGenderCounts] = useState<{ male: number; female: number } | null>(null);
  const [guestLoading, setGuestLoading] = useState(false);
  const [capacity, setCapacity] = useState<any>(null);
  const nav = useNavigate();
  const { refresh: refreshSession } = useSession();

  // 加载小镇容量(满员时禁用注册/游客入口并给出解释)
  useEffect(() => {
    api.status().then((r) => setCapacity(r.capacity)).catch(() => {});
  }, []);

  // 性别默认值:按全镇男女比例取较少的一方;平衡则随机
  useEffect(() => {
    api.genderOptions().then((r) => {
      setGenderCounts(r.counts);
      setGuestGender(r.suggest ?? (Math.random() < 0.5 ? "male" : "female"));
    }).catch(() => {});
  }, []);

  const enter = async (res: { token?: string; hasAgent?: boolean; role?: string; mustChangePassword?: boolean }) => {
    if (!res.token) return;
    setToken(res.token);
    markLoggedIn();
    // SessionProvider 只在应用启动时自动拉取身份。登录/验证后必须先刷新，
    // 否则 RoleGuard 会沿用“未登录”的旧状态并把用户送回登录页。
    await refreshSession();
    if (res.mustChangePassword) {
      nav("/change-password");
    } else if (res.role === "super_admin") {
      nav("/admin/overview");
    } else if (res.role === "mayor") {
      nav("/console/overview");
    } else {
      const user = await refreshSession();
      setActiveTownId(user?.homeTownId || "");
      nav(res.hasAgent && user?.homeTownId ? `/town/${user.homeTownId}` : `/create-agent${user?.homeTownId ? `?townId=${user.homeTownId}` : ""}`);
    }
  };

  // 注册前本地校验,避免把错误输入发给服务器
  const validateRegister = (): boolean => {
    const errs: FieldErrors = {};
    if (!EMAIL_RE.test(email.trim())) errs.email = "请输入正确的邮箱地址";
    const uname = username.trim();
    if (uname.length < 2 || uname.length > 20) errs.username = "用户名需2-20个字符";
    if (password.length < 4 || password.length > 64) errs.password = "密码需4-64位";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const clearFieldError = (k: keyof FieldErrors) =>
    setFieldErrors((f) => (f[k] ? { ...f, [k]: undefined } : f));

  const submit = async () => {
    setError(""); setInfo("");
    if (mode === "register") {
      // 检测到邮箱和用户名填写反了:自动交换并提示,避免 400
      if (!EMAIL_RE.test(email) && EMAIL_RE.test(username)) {
        setEmail(username);
        setUsername(email);
        setInfo("检测到邮箱和用户名填写反了，已自动调整，请确认后再次点击注册");
        return;
      }
      if (!validateRegister()) return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        const res = await api.register(email.trim(), username.trim(), password);
        setEmail(res.email || email);
        setInfo(res.mailSent ? `验证码已发送到 ${res.email}，请查收邮件` : "验证码邮件发送失败，可点击重发");
        setView("verify");
      } else {
        const res = await api.login(username, password); // account: 用户名或邮箱
        await enter(res);
      }
    } catch (e: any) {
      if (e.needsVerify) {
        setEmail(e.email);
        setInfo("邮箱尚未验证，输入验证码完成验证");
        setView("verify");
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async () => {
    setError(""); setInfo("");
    setLoading(true);
    try {
      const res = await api.verifyEmail(email, code);
      await enter(res);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError(""); setInfo("");
    try {
      await api.resendVerify(email);
      setInfo(`验证码已重新发送到 ${email}`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const submitForgot = async () => {
    setError(""); setInfo("");
    setLoading(true);
    try {
      if (!codeSent) {
        const res = await api.forgotPassword(email);
        setCodeSent(true);
        setInfo(res.ok ? `重置验证码已发送到 ${email}（如该邮箱已注册）` : "发送失败");
      } else {
        const res = await api.resetPassword(email, code, newPassword);
        setInfo("密码已重置，请用新密码登录");
        setView("auth");
        setMode("login");
        setPassword("");
        setNewPassword("");
        setCode("");
        setCodeSent(false);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const guestJoin = async () => {
    setError(""); setInfo("");
    setGuestLoading(true);
    try {
      const res = await api.guestJoin(guestName, guestGender);
      setToken(res.token);
      await refreshSession();
      setActiveTownId(res.townId || "");
      nav(res.townId ? `/town/${res.townId}` : "/town");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGuestLoading(false);
    }
  };

  const shuffleName = () => {
    const n = GUEST_SAMPLE_NAMES[Math.floor(Math.random() * GUEST_SAMPLE_NAMES.length)];
    setGuestName(n);
  };

  const inputCls = "w-full mb-3 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-[#1D1D1D]";
  const playersFull = !!capacity && capacity.players >= capacity.maxPlayers;
  const agentsFull = !!capacity && capacity.agents >= capacity.maxAgents;

  return (
    <div className="h-full overflow-y-auto flex"
      style={{ background: "linear-gradient(180deg, #10173a 0%, #1b2a5e 100%)" }}>
      <div className="m-auto w-full max-w-sm px-6 py-8">
        <Link to="/" className="block text-center text-4xl font-black text-[#F8F7E5] mb-2"
          style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>星原小镇</Link>
        <p className="text-center text-[#8fa0d8] text-sm mb-8">
          {view === "verify" ? "验证你的邮箱" : view === "forgot" ? "重置密码" : mode === "register" ? "注册一个账号,许下你的心愿" : "欢迎回来,小镇想你了"}
        </p>

        <div className="rounded-2xl p-6" style={{ background: "#F8F7E5", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
          {view === "auth" && (
            <>
              <div className="flex mb-5 rounded-full p-1" style={{ background: "#ebe6cf" }}>
                {(["register", "login"] as const).map((m) => (
                  <button key={m} onClick={() => setMode(m)}
                    className={`flex-1 py-1.5 rounded-full text-sm font-bold transition-colors ${mode === m ? "text-[#1D1D1D]" : "text-[#8a8571]"}`}
                    style={mode === m ? { background: "#e8c96a" } : {}}>
                    {m === "register" ? "注册" : "登录"}
                  </button>
                ))}
              </div>
              {mode === "register" && (
                <>
                  {playersFull && (
                    <div className="mb-3 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                      小镇注册名额已满({capacity.maxPlayers} 人),暂时无法注册新账号,请稍后再来。
                    </div>
                  )}
                  <input value={username}
                    onChange={(e) => { setUsername(e.target.value); clearFieldError("username"); }}
                    placeholder="用户名(自由设定)" maxLength={20} className={inputCls} />
                  {fieldErrors.username && <p className="-mt-2 mb-3 text-xs text-red-600">{fieldErrors.username}</p>}
                </>
              )}
              <input value={mode === "register" ? email : username}
                onChange={(e) => mode === "register"
                  ? (setEmail(e.target.value), clearFieldError("email"))
                  : setUsername(e.target.value)}
                placeholder={mode === "register" ? "邮箱(作为账号 ID)" : "用户名或邮箱"}
                maxLength={64} className={inputCls} />
              {mode === "register" && fieldErrors.email &&
                <p className="-mt-2 mb-3 text-xs text-red-600">{fieldErrors.email}</p>}
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="密码"
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="w-full mb-4 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-[#1D1D1D]" />
              {fieldErrors.password && <p className="-mt-3 mb-3 text-xs text-red-600">{fieldErrors.password}</p>}
              {info && <div className="mb-3 text-sm text-emerald-700">{info}</div>}
              {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
              <button onClick={submit} disabled={loading || playersFull || !password || !username || (mode === "register" && !email)}
                className="w-full py-3 rounded-xl font-bold text-[#1D1D1D] disabled:opacity-50 transition-transform hover:scale-[1.02]"
                style={{ background: "#e8c96a", boxShadow: "0 4px 0 #b39c4f" }}>
                {loading ? "……" : mode === "register" ? "✨ 开始我的星原之旅" : "回到小镇"}
              </button>
              {mode === "login" && (
                <button onClick={() => { setView("forgot"); setError(""); setInfo(""); setNewPassword(""); setCode(""); setCodeSent(false); }}
                  className="mt-3 w-full text-center text-xs text-[#8a8571] underline">
                  忘记密码?通过邮箱重置
                </button>
              )}
            </>
          )}

          {view === "verify" && (
            <>
              <p className="text-xs leading-relaxed text-[#8a8571] mb-3">
                验证码已发送到 <span className="font-bold text-[#1D1D1D]">{email}</span>，10 分钟内有效。
              </p>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 位验证码" maxLength={6}
                onKeyDown={(e) => e.key === "Enter" && submitVerify()}
                className="w-full mb-3 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-[#1D1D1D] text-center text-lg tracking-[0.5em]" />
              {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
              {info && <div className="mb-3 text-sm text-emerald-700">{info}</div>}
              <button onClick={submitVerify} disabled={loading || code.length !== 6}
                className="w-full py-3 rounded-xl font-bold text-[#1D1D1D] disabled:opacity-50"
                style={{ background: "#e8c96a", boxShadow: "0 4px 0 #b39c4f" }}>
                {loading ? "……" : "完成验证,进入小镇"}
              </button>
              <div className="mt-3 flex justify-between text-xs">
                <button onClick={resend} className="text-[#8a8571] underline">重新发送验证码</button>
                <button onClick={() => { setView("auth"); setError(""); setInfo(""); }} className="text-[#8a8571] underline">返回</button>
              </div>
            </>
          )}

          {view === "forgot" && (
            <>
              <p className="text-xs leading-relaxed text-[#8a8571] mb-3">
                输入注册邮箱,我们将发送 6 位重置验证码。先获取验证码,再设置新密码。
              </p>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="注册邮箱"
                maxLength={64} className={inputCls} />
              {codeSent && (
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 位验证码" maxLength={6}
                  onKeyDown={(e) => e.key === "Enter" && submitForgot()}
                  className="w-full mb-3 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-[#1D1D1D] text-center tracking-[0.4em]" />
              )}
              <input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type="password"
                placeholder="新密码(至少4位)"
                onKeyDown={(e) => e.key === "Enter" && submitForgot()}
                className="w-full mb-3 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-[#1D1D1D]" />
              {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
              {info && <div className="mb-3 text-sm text-emerald-700">{info}</div>}
              <button onClick={submitForgot} disabled={loading || !email}
                className="w-full py-3 rounded-xl font-bold text-[#1D1D1D] disabled:opacity-50"
                style={{ background: "#e8c96a", boxShadow: "0 4px 0 #b39c4f" }}>
                {loading ? "……" : codeSent ? "重置密码" : "发送验证码"}
              </button>
              <div className="mt-3 text-center">
                <button onClick={() => { setView("auth"); setError(""); setInfo(""); setCodeSent(false); }} className="text-xs text-[#8a8571] underline">返回登录</button>
              </div>
            </>
          )}

          {view === "auth" && (
            <div className="mt-5 pt-4 border-t border-[#ddd5b8]">
              <button onClick={() => setGuestExpanded(!guestExpanded)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-[#b45309] bg-[#fef3e2] hover:bg-[#fde8c8] transition-colors">
                <span className="text-base">🚀</span> 游客快速进入
                <span className="text-xs text-[#8a8571]">{guestExpanded ? "收起" : "展开"}</span>
              </button>
              {guestExpanded && (
                <div className="mt-3">
                  {agentsFull && (
                    <div className="mb-3 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                      小镇居民已满({capacity.maxAgents} 人),暂时无法以游客身份加入,请稍后再来。
                    </div>
                  )}
                  <p className="text-xs leading-relaxed text-[#8a8571] mb-2">
                    无需注册,一键进入小镇体验。游客身份 24 小时后会自动离开;期间可在留言板留言 2 条。
                  </p>
                  <div className="flex gap-2 mb-2">
                    <input value={guestName} onChange={(e) => setGuestName(e.target.value)}
                      placeholder="给自己起个名字(留空则随机)" maxLength={12}
                      onKeyDown={(e) => e.key === "Enter" && guestJoin()}
                      className="flex-1 px-3 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#f59e0b] text-[#1D1D1D]" />
                    <button onClick={shuffleName} title="换个随机名字"
                      className="px-3 rounded-xl font-bold text-[#b45309] bg-[#fde8c8] hover:bg-[#fcd7a0]">
                      🎲
                    </button>
                  </div>
                  <div className="flex gap-2 mb-1">
                    {([["male", "♂ 男"], ["female", "♀ 女"]] as const).map(([g, label]) => (
                      <button key={g} type="button" onClick={() => setGuestGender(g)}
                        className={`flex-1 px-3 py-2 rounded-xl border text-sm font-bold transition-colors ${guestGender === g
                          ? (g === "male" ? "border-[#0284c7] bg-[#e0f2fe] text-[#0369a1]" : "border-[#db2777] bg-[#fce7f3] text-[#be185d]")
                          : "border-[#ddd5b8] bg-white text-[#6b6252]"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#8a8571] mb-2">
                    小镇当前性别分布:♂ {genderCounts?.male ?? "-"} / ♀ {genderCounts?.female ?? "-"};性别创建后不可修改
                  </p>
                  <button onClick={guestJoin} disabled={guestLoading || agentsFull}
                    className="w-full py-2.5 rounded-xl font-bold text-white disabled:opacity-50 transition-transform hover:scale-[1.02]"
                    style={{ background: agentsFull ? "#9ca3af" : "#f97316", boxShadow: agentsFull ? "0 4px 0 #6b7280" : "0 4px 0 #c2560a" }}>
                    {guestLoading ? "进入中……" : agentsFull ? "小镇居民已满" : "以游客身份进入小镇"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
